import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Zap, Plus, Search, Filter, MoreHorizontal, PlayCircle, Clock, CheckCircle,
  AlertCircle, Pause, Copy, Edit2, Trash2, History, Settings, Users, Globe,
  Lock, Calendar, Webhook, Activity, ChevronRight, Eye, Send, XCircle,
  RefreshCw, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { cn } from '../lib/utils';

const workflowTypes = {
  helpdesk: { label: 'Help Desk', icon: Users, description: 'Triggered by user requests' },
  team_only: { label: 'Team Only', icon: Lock, description: 'Limited to specific teams' },
  scheduled: { label: 'Scheduled', icon: Calendar, description: 'Runs on schedule' },
  webhook: { label: 'Webhook', icon: Webhook, description: 'Triggered by external events' },
  event: { label: 'Event-Triggered', icon: Activity, description: 'Triggered by system events' },
  access: { label: 'Access Provisioning', icon: Zap, description: 'JML lifecycle automation' }
};

const scopeConfig = {
  org_wide: { label: 'Org-wide', icon: Globe, color: 'text-blue-600' },
  team_only: { label: 'Team Only', icon: Lock, color: 'text-amber-600' }
};

const statusConfig = {
  published: { label: 'Published', color: 'bg-green-100 text-green-800 border-green-200' },
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-600 border-slate-200' },
  testing: { label: 'Testing', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  disabled: { label: 'Disabled', color: 'bg-red-100 text-red-800 border-red-200' }
};

const mockWorkflows = [
  {
    id: 'wf-1',
    name: 'Password Reset',
    description: 'Self-service password reset with identity verification',
    type: 'helpdesk',
    scope: 'org_wide',
    status: 'published',
    version: 'v2.1',
    team: 'IT Operations',
    actions: 4,
    runCount7d: 234,
    successRate: 98.2,
    avgDuration: '2m 15s',
    lastRun: '5 min ago',
    createdBy: 'Emily Zhang',
    lastModified: '2024-01-10'
  },
  {
    id: 'wf-2',
    name: 'New Hire Onboarding',
    description: 'Complete provisioning for new employees',
    type: 'access',
    scope: 'org_wide',
    status: 'published',
    version: 'v3.0',
    team: 'IT Operations',
    actions: 12,
    runCount7d: 45,
    successRate: 95.5,
    avgDuration: '15m 30s',
    lastRun: '2 hours ago',
    createdBy: 'David Park',
    lastModified: '2024-01-12'
  },
  {
    id: 'wf-3',
    name: 'Elevated Access Request',
    description: 'Temporary admin access with security review',
    type: 'helpdesk',
    scope: 'team_only',
    status: 'published',
    version: 'v1.5',
    team: 'Security',
    actions: 6,
    runCount7d: 28,
    successRate: 100,
    avgDuration: '8m 45s',
    lastRun: '1 day ago',
    createdBy: 'Alex Kim',
    lastModified: '2024-01-08'
  },
  {
    id: 'wf-4',
    name: 'License Reclamation',
    description: 'Identify and revoke unused SaaS licenses',
    type: 'scheduled',
    scope: 'org_wide',
    status: 'published',
    version: 'v1.2',
    team: 'IT Operations',
    actions: 5,
    runCount7d: 7,
    successRate: 100,
    avgDuration: '45m',
    lastRun: '1 day ago',
    schedule: 'Weekly on Monday 6:00 AM',
    createdBy: 'Taylor Brown',
    lastModified: '2024-01-05'
  },
  {
    id: 'wf-5',
    name: 'Security Alert Response',
    description: 'Automated containment for suspicious activity',
    type: 'event',
    scope: 'org_wide',
    status: 'published',
    version: 'v2.0',
    team: 'Security',
    actions: 8,
    runCount7d: 12,
    successRate: 91.6,
    avgDuration: '1m 30s',
    lastRun: '4 hours ago',
    createdBy: 'Alex Kim',
    lastModified: '2024-01-14'
  },
  {
    id: 'wf-6',
    name: 'GitHub Access Provisioning',
    description: 'Add/remove users from GitHub organization',
    type: 'helpdesk',
    scope: 'team_only',
    status: 'testing',
    version: 'v0.9',
    team: 'Identity & Access',
    actions: 4,
    runCount7d: 5,
    successRate: 80,
    avgDuration: '3m',
    lastRun: '30 min ago',
    createdBy: 'Jordan Lee',
    lastModified: '2024-01-15'
  },
  {
    id: 'wf-7',
    name: 'Employee Offboarding',
    description: 'Complete deprovisioning for departing employees',
    type: 'access',
    scope: 'org_wide',
    status: 'draft',
    version: 'v0.5',
    team: 'IT Operations',
    actions: 15,
    runCount7d: 0,
    successRate: null,
    avgDuration: null,
    lastRun: null,
    createdBy: 'Emily Zhang',
    lastModified: '2024-01-15'
  }
];

const mockRunHistory = [
  { id: 'run-1', workflow: 'Password Reset', status: 'success', duration: '2m 10s', timestamp: '5 min ago', triggeredBy: 'Sarah Chen' },
  { id: 'run-2', workflow: 'Security Alert Response', status: 'success', duration: '1m 45s', timestamp: '4 hours ago', triggeredBy: 'System Event' },
  { id: 'run-3', workflow: 'GitHub Access Provisioning', status: 'failed', duration: '45s', timestamp: '30 min ago', triggeredBy: 'Taylor Brown', error: 'API rate limit exceeded' },
  { id: 'run-4', workflow: 'New Hire Onboarding', status: 'success', duration: '14m 22s', timestamp: '2 hours ago', triggeredBy: 'HR System' },
  { id: 'run-5', workflow: 'License Reclamation', status: 'success', duration: '42m', timestamp: '1 day ago', triggeredBy: 'Schedule' }
];

export const WorkflowLibrary = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('library');
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [showPublishDialog, setShowPublishDialog] = useState(false);

  const filteredWorkflows = useMemo(() => {
    return mockWorkflows.filter(wf => {
      const matchesSearch = wf.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        wf.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = selectedType === 'all' || wf.type === selectedType;
      const matchesStatus = selectedStatus === 'all' || wf.status === selectedStatus;
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [searchQuery, selectedType, selectedStatus]);

  const stats = {
    total: mockWorkflows.length,
    published: mockWorkflows.filter(w => w.status === 'published').length,
    runsToday: mockWorkflows.reduce((sum, w) => sum + Math.floor(w.runCount7d / 7), 0),
    avgSuccess: Math.round(
      mockWorkflows.filter(w => w.successRate).reduce((sum, w) => sum + w.successRate, 0) /
      mockWorkflows.filter(w => w.successRate).length
    )
  };

  const handleTestRun = (workflow) => {
    setSelectedWorkflow(workflow);
    setShowTestDialog(true);
  };

  const handlePublish = (workflow) => {
    setSelectedWorkflow(workflow);
    setShowPublishDialog(true);
  };

  return (
    <div className="min-h-full bg-background">
      <div className="border-b bg-card">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Workflows</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Build, test, and publish automation workflows
              </p>
            </div>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Create Workflow
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total Workflows</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.published}</p>
                  <p className="text-xs text-muted-foreground">Published</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <PlayCircle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.runsToday}</p>
                  <p className="text-xs text-muted-foreground">Runs/day avg</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.avgSuccess}%</p>
                  <p className="text-xs text-muted-foreground">Avg Success Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="library">Library</TabsTrigger>
              <TabsTrigger value="runs">Run History</TabsTrigger>
            </TabsList>

            {activeTab === 'library' && (
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search workflows..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 w-64"
                  />
                </div>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {Object.entries(workflowTypes).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {Object.entries(statusConfig).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <TabsContent value="library" className="space-y-4">
            {filteredWorkflows.map((workflow) => {
              const type = workflowTypes[workflow.type];
              const scope = scopeConfig[workflow.scope];
              const status = statusConfig[workflow.status];
              const TypeIcon = type.icon;
              const ScopeIcon = scope.icon;

              return (
                <Card key={workflow.id} className="hover:border-primary/50 transition-colors">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <TypeIcon className="w-5 h-5 text-primary" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-base font-semibold text-foreground">{workflow.name}</h3>
                          <Badge variant="outline" className="text-xs">{workflow.version}</Badge>
                          <Badge variant="outline" className={cn("text-xs", status.color)}>
                            {status.label}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            <ScopeIcon className={cn("w-3 h-3 mr-1", scope.color)} />
                            {scope.label}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{workflow.description}</p>

                        <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {workflow.team}
                          </span>
                          <span className="flex items-center gap-1">
                            <Zap className="w-3 h-3" />
                            {workflow.actions} actions
                          </span>
                          {workflow.runCount7d > 0 && (
                            <>
                              <span className="flex items-center gap-1">
                                <PlayCircle className="w-3 h-3" />
                                {workflow.runCount7d} runs (7d)
                              </span>
                              <span className="flex items-center gap-1">
                                <CheckCircle className="w-3 h-3 text-green-500" />
                                {workflow.successRate}% success
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {workflow.avgDuration} avg
                              </span>
                            </>
                          )}
                          {workflow.schedule && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {workflow.schedule}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {workflow.status === 'draft' || workflow.status === 'testing' ? (
                          <>
                            <Button variant="outline" size="sm" onClick={() => handleTestRun(workflow)}>
                              <PlayCircle className="w-4 h-4 mr-2" aria-hidden="true" />
                              Test Run
                            </Button>
                            {workflow.status === 'testing' && (
                              <Button size="sm" onClick={() => handlePublish(workflow)}>
                                <Send className="w-4 h-4 mr-2" aria-hidden="true" />
                                Publish
                              </Button>
                            )}
                          </>
                        ) : (
                          <Button variant="outline" size="sm" onClick={() => navigate(`/workflow-library/${workflow.id}`)}>
                            <Eye className="w-4 h-4 mr-2" aria-hidden="true" />
                            View
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <Edit2 className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Copy className="w-4 h-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/workflow-library/${workflow.id}/runs`)}>
                              <History className="w-4 h-4 mr-2" />
                              Run History
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Settings className="w-4 h-4 mr-2" />
                              Settings
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {workflow.status === 'published' && (
                              <DropdownMenuItem className="text-amber-600">
                                <Pause className="w-4 h-4 mr-2" />
                                Disable
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem className="text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>

          <TabsContent value="runs">
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Workflow</th>
                      <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Status</th>
                      <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Duration</th>
                      <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Triggered By</th>
                      <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Time</th>
                      <th className="w-12 p-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {mockRunHistory.map((run) => (
                      <tr key={run.id} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <Zap className="w-4 h-4 text-primary" />
                            <span className="text-sm font-medium text-foreground">{run.workflow}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-xs",
                              run.status === 'success' && "bg-green-100 text-green-800 border-green-200",
                              run.status === 'failed' && "bg-red-100 text-red-800 border-red-200"
                            )}
                          >
                            {run.status === 'success' ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                            {run.status}
                          </Badge>
                          {run.error && (
                            <p className="text-xs text-red-600 mt-1">{run.error}</p>
                          )}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">{run.duration}</td>
                        <td className="p-4 text-sm text-muted-foreground">{run.triggeredBy}</td>
                        <td className="p-4 text-sm text-muted-foreground">{run.timestamp}</td>
                        <td className="p-4">
                          <Button variant="ghost" size="sm">
                            <FileText className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlayCircle className="w-5 h-5 text-primary" />
              Test Run: {selectedWorkflow?.name}
            </DialogTitle>
            <DialogDescription>
              Run this workflow in test mode. No changes will be made to production systems.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="text-sm font-medium text-foreground mb-2">Test mode behavior:</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• All API calls will be simulated (dry-run)</li>
                <li>• Approval steps will auto-approve</li>
                <li>• Results logged but no external changes</li>
              </ul>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Test Input (Optional)</label>
              <Input placeholder="e.g., user email or ticket ID" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestDialog(false)}>Cancel</Button>
            <Button onClick={() => setShowTestDialog(false)}>
              <PlayCircle className="w-4 h-4 mr-2" />
              Start Test Run
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" />
              Publish: {selectedWorkflow?.name}
            </DialogTitle>
            <DialogDescription>
              This workflow will be available for production use.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Publish Scope</label>
              <Select defaultValue="team_only">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="team_only">
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Team Only (Recommended for new workflows)
                    </div>
                  </SelectItem>
                  <SelectItem value="org_wide">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Organization-wide
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Publishing requires approval from a team manager.
                The workflow will be submitted for review.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPublishDialog(false)}>Cancel</Button>
            <Button onClick={() => setShowPublishDialog(false)}>
              <Send className="w-4 h-4 mr-2" />
              Submit for Approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkflowLibrary;
